import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import {
  TableHeader,
  TableHost,
  ImageCell,
  DateCell,
  TableCell,
  TextCell,
} from '@/components/Table'
import { TableEmptyState } from '@/components/Table/TableEmptyState'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from '@/components/TableSearchbar'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import { ContentError } from '@/components/ContentError'
import { FilterControlsToggleButton } from '@/components/FilterControlsToggleButton'
import {
  ContextCell,
  AmountCell,
} from '@/features/payouts/components/PayoutTransactionsTableCells'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import { byDesign } from '@/infra/design'
import type { Id } from '@/infra/id'
import imgSearchNoresultsUrl from '@/assets/misc/img_search-noresults.svg'
import SearchNoResultsIllustration from '@/components/SpotIllustration/SearchNoResultsIllustration'
import imgJobCompletedUrl from '@/assets/misc/img_job-completed.svg'
import CompleteJobIllustration from '@/components/SpotIllustration/CompleteJobIllustration'

export interface ProHistoricalTransactionsTableProps {
  searchText: string
  onSearchTextChange: (value: string) => unknown
  filtersShowing: boolean
  onToggleFilters: () => void
  appliedFilterCount: number
  isExportPending: boolean
  onExportCsv: () => unknown
  filterControls: ReactNode
  showTable: boolean
  error?: unknown
  appliedFilters: boolean
  onClearFilters: () => void
  children?: ReactNode
}

export function ProHistoricalTransactionsTable({
  searchText,
  onSearchTextChange,
  filtersShowing,
  onToggleFilters,
  appliedFilterCount,
  isExportPending,
  onExportCsv,
  filterControls,
  showTable,
  error,
  appliedFilters,
  onClearFilters,
  children,
}: ProHistoricalTransactionsTableProps) {
  return (
    <>
      <TableHost>
        <TableSearchbar
          searchText={searchText}
          onSearchTextChange={onSearchTextChange}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing}
              count={appliedFilterCount}
              onPress={onToggleFilters}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              button={<IconButton name="more" label="Transaction actions" />}
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                isDisabled={isExportPending}
                icon={<Icon name="download" />}
                onPress={onExportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        {filterControls}

        {showTable ? (
          children
        ) : error ? (
          <ContentError size="lg" error={error} />
        ) : (
          <HistoricalTransactionsEmptyState
            appliedFilters={appliedFilters}
            onClearFilters={onClearFilters}
          />
        )}
      </TableHost>
    </>
  )
}

export function ProHistoricalTransactionsTableHeader() {
  return (
    <>
      <TableHeader align="left">Date</TableHeader>
      <TableHeader align="left">Workspace</TableHeader>
      <TableHeader align="left">Type</TableHeader>
      <TableHeader align="left">Job name</TableHeader>
      <TableHeader align="right">Amount</TableHeader>
      <TableHeader align="right"></TableHeader>
    </>
  )
}

export interface ProHistoricalTransactionRowProps {
  id: Id
  completedAt: Date
  workspaceName: string
  workspaceIcon: string
  source: TransactionSourceType
  memo: string | null
  reason: UserTransactionReason | null
  jobShortId?: string
  jobDisplayName?: string
  hasJob: boolean
  amount: number
  onViewJob: () => void
}

export function ProHistoricalTransactionRow({
  id,
  completedAt,
  workspaceName,
  workspaceIcon,
  source,
  memo,
  reason,
  jobShortId,
  jobDisplayName,
  hasJob,
  amount,
  onViewJob,
}: ProHistoricalTransactionRowProps) {
  return (
    <>
      <DateCell
        date={completedAt}
        testId={`pro-historical-transaction-date-completed-${id}`}
      />
      <ImageCell title={workspaceName} image={workspaceIcon} minWidth={110} />
      <ContextCell
        source={source}
        userTransactionMemo={memo}
        userTransactionReason={reason}
        jobTransactionJobId={jobShortId}
      />
      <TextCell testId={`pro-historical-transaction-job-name-${id}`}>
        {jobDisplayName}
      </TextCell>
      <AmountCell neutral amount={amount} />
      <TableCell align="right">
        {hasJob && (
          <Dropdown
            autoDismiss
            button={
              // Needs to be inline, otherwise it doesn't align
              // correctly with the `more` button in the searchbar.
              <IconButton name="more" label="Transaction actions" inline />
            }
          >
            <DropdownTitle>Actions</DropdownTitle>
            <DropdownItem onPress={onViewJob} icon={<Icon name="view" />}>
              View job
            </DropdownItem>
          </Dropdown>
        )}
      </TableCell>
    </>
  )
}

interface HistoricalTransactionsEmptyStateProps {
  appliedFilters: boolean
  onClearFilters: () => void
}

function HistoricalTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: HistoricalTransactionsEmptyStateProps) {
  return (
    <TableEmptyState bordered>
      {appliedFilters ? (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgSearchNoresultsUrl}
            illustration={byDesign(undefined, SearchNoResultsIllustration)}
          />
          <EmptyStateTitle>
            We couldn't find what you were looking for.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Maybe try{' '}
            <Button
              label="clearing your search filter"
              size="baseline"
              variant="text"
              onPress={onClearFilters}
            />
            ?
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <EmptyState size="sm">
          <EmptyStateIllustration
            src={imgJobCompletedUrl}
            illustration={byDesign(undefined, CompleteJobIllustration)}
          />
          <EmptyStateTitle>No transactions paid out yet.</EmptyStateTitle>
          <EmptyStateDescription>
            When you receive your first payout, the paid transactions will
            appear here.
          </EmptyStateDescription>
        </EmptyState>
      )}
    </TableEmptyState>
  )
}
